/**
 * Returns the scroll progress (0-1) of a scroll container.
 *
 * Derives progress directly from `scrollTop / maxScroll` on the container
 * element, so it works for both virtualized and non-virtualized lists.
 *
 * Returns `null` when the content doesn't overflow (nothing to scrub).
 */

import { RefObject, useCallback, useEffect, useState } from "react";

export function useScrubberProgress(
  scrollRef: RefObject<HTMLElement | null>
): [number | null, (progress: number) => void] {
  const [progress, setProgress] = useState<number | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) {
        setProgress(null);
        return;
      }
      setProgress(Math.min(1, Math.max(0, el.scrollTop / maxScroll)));
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  }, [scrollRef]);

  const scrollTo = useCallback(
    (target: number) => {
      const el = scrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTo({ top: target * maxScroll });
    },
    [scrollRef]
  );

  return [progress, scrollTo];
}
